// components/VoiceMessageBubble.js
import React, { useState, useRef, useEffect } from "react";
import { FiPlay, FiPause } from "react-icons/fi";
import clsx from "clsx";

export default function VoiceMessageBubble({ url, disabled = false }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const stopOtherAudios = () => {
    const event = new CustomEvent("pauseAllAudios");
    window.dispatchEvent(event);
  };

  const togglePlay = () => {
    if (disabled || !audioRef.current) return;

    if (!isPlaying) {
      stopOtherAudios();
      audioRef.current.play();
      setIsPlaying(true);
    } else {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  useEffect(() => {
    const audio = audioRef.current;

    const onEnded = () => setIsPlaying(false);
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => setDuration(audio.duration);

    const handlePauseAll = () => {
      if (audio && !audio.paused) {
        audio.pause();
        audio.currentTime = 0;
        setIsPlaying(false);
      }
    };

    audio?.addEventListener("ended", onEnded);
    audio?.addEventListener("timeupdate", onTimeUpdate);
    audio?.addEventListener("loadedmetadata", onLoadedMetadata);
    window.addEventListener("pauseAllAudios", handlePauseAll);

    return () => {
      audio?.removeEventListener("ended", onEnded);
      audio?.removeEventListener("timeupdate", onTimeUpdate);
      audio?.removeEventListener("loadedmetadata", onLoadedMetadata);
      window.removeEventListener("pauseAllAudios", handlePauseAll);
    };
  }, []);

  return (
    <div className="px-3 py-1.5 mt-2 mb-2 bg-[#8d36f9] rounded-xl max-w-xl">
      <div className="flex items-center rounded-2xl px-3 py-2">
        <button
          onClick={togglePlay}
          className="text-white w-6 h-6 flex items-center justify-center"
        >
          {isPlaying ? <FiPause /> : <FiPlay />}
        </button>
        <div className="px-3 pr-4 text-white text-sm">
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>
        <div className="flex gap-[2px] items-end flex-1">
          {[4, 12, 18, 20, 18, 12, 8].map((h, i) => (
            <div
              key={i}
              className={clsx(
                "w-[3px] bg-white rounded-full",
                isPlaying && "animate-wave"
              )}
              style={{ height: `${h}px`, animationDelay: `${i * 0.1}s` }}
            />
          ))}
        </div>
      </div>
      <audio ref={audioRef} src={url} />
    </div>
  );
}
